type CookieSameSite = 'lax' | 'strict' | 'none';

type CookieOptionsInput = {
  domain?: unknown;
  expires?: unknown;
  maxAge?: unknown;
  partitioned?: unknown;
  path?: unknown;
  sameSite?: unknown;
  secure?: unknown;
};

export type SafeCookieOptions = {
  domain?: string;
  expires?: Date;
  maxAge?: number;
  partitioned?: boolean;
  path: string;
  sameSite?: CookieSameSite;
  secure?: boolean;
};

const COOKIE_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

const normalizeSameSite = (sameSite: unknown): CookieSameSite | undefined => {
  if (typeof sameSite !== 'string') return undefined;

  const normalized = sameSite.trim().toLowerCase();
  if (normalized === 'lax' || normalized === 'strict' || normalized === 'none') {
    return normalized;
  }

  return undefined;
};

const normalizePath = (path: unknown): string => {
  if (typeof path !== 'string') return '/';

  const normalized = path.trim();
  if (!normalized || normalized.includes(';')) return '/';
  return normalized.startsWith('/') ? normalized : '/';
};

const normalizeDomain = (domain: unknown): string | undefined => {
  if (typeof domain !== 'string') return undefined;

  const normalized = domain.trim();
  if (!normalized || /[;\s]/.test(normalized)) return undefined;
  return normalized;
};

const normalizeMaxAge = (maxAge: unknown): number | undefined => {
  const numericMaxAge = typeof maxAge === 'number' ? maxAge : Number(maxAge);

  if (!Number.isFinite(numericMaxAge)) return undefined;
  return Math.max(0, Math.floor(numericMaxAge));
};

const normalizeExpires = (expires: unknown): Date | undefined => {
  if (expires == null) return undefined;

  const date = expires instanceof Date ? expires : new Date(expires as string | number);
  if (Number.isNaN(date.getTime())) return undefined;
  return date;
};

export const isValidCookieName = (name: unknown): name is string => {
  if (typeof name !== 'string') return false;

  const normalized = name.trim();
  return Boolean(normalized) && COOKIE_NAME_PATTERN.test(normalized);
};

export const sanitizeCookieOptions = (
  options?: CookieOptionsInput,
  defaults?: Pick<SafeCookieOptions, 'path' | 'sameSite' | 'secure' | 'partitioned'>
): SafeCookieOptions => {
  const sameSite = normalizeSameSite(options?.sameSite) ?? defaults?.sameSite;
  const secure = typeof options?.secure === 'boolean' ? options.secure : defaults?.secure;

  return {
    path: normalizePath(options?.path ?? defaults?.path),
    sameSite,
    secure,
    partitioned:
      typeof options?.partitioned === 'boolean'
        ? options.partitioned
        : defaults?.partitioned,
    domain: normalizeDomain(options?.domain),
    maxAge: normalizeMaxAge(options?.maxAge),
    expires: normalizeExpires(options?.expires),
  };
};

export const buildDocumentCookieString = (
  name: unknown,
  value: string,
  options?: CookieOptionsInput,
  defaults?: Pick<SafeCookieOptions, 'path' | 'sameSite' | 'secure' | 'partitioned'>
) => {
  if (!isValidCookieName(name)) return null;

  const sanitized = sanitizeCookieOptions(options, defaults);
  const segments = [`${name}=${encodeURIComponent(value)}`, `Path=${sanitized.path}`];

  if (typeof sanitized.maxAge === 'number') {
    segments.push(`Max-Age=${sanitized.maxAge}`);
  }

  if (sanitized.expires) {
    segments.push(`Expires=${sanitized.expires.toUTCString()}`);
  }

  if (sanitized.sameSite) {
    const formattedSameSite =
      sanitized.sameSite === 'none'
        ? 'None'
        : sanitized.sameSite === 'strict'
          ? 'Strict'
          : 'Lax';
    segments.push(`SameSite=${formattedSameSite}`);
  }

  if (sanitized.secure) {
    segments.push('Secure');
  }

  if (sanitized.domain) {
    segments.push(`Domain=${sanitized.domain}`);
  }

  if (sanitized.partitioned) {
    segments.push('Partitioned');
  }

  return segments.join('; ');
};